/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ChartsHelper } from '@app/app/shared/helpers/charts-helper';
import { UserDetails } from '@app/app/user-auth/models';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { Select } from '@ngxs/store';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { LocalTimeHelper } from '../../../../helpers/local-time-helper';
import { ChartContent, ChartItem, ChartLearner, ChartVersionScore } from '../../../../models/editor/chart-content.model';

@Component({
  selector: 'ptl-polar-chart-preview',
  templateUrl: './polar-chart-preview.component.html',
  styleUrls: ['./polar-chart-preview.component.scss'],
})
export class PolarChartPreviewComponent implements OnInit {
  /** Receives the Form object */
  @Input() chartContent: ChartContent;

  @Input() chartVersionStart: number;
  @Input() chartVersionEnd: number;

  @Input() learner: ChartLearner;

  @Select(UserAuthState.userDetailsData)
  userDetailsData$: Observable<UserDetails>;

  learner$: Observable<ChartLearner>;

  @ViewChild('Chart') chart: ElementRef;
  @ViewChild('circlePoints') circlePoints: ElementRef;
  @ViewChild('circleLines') circleLines: ElementRef;

  chartId = new Date().getTime();

  chartData: ChartDataset[] = [];
  chartDataColors: string[] = [];
  chartDataAlphaColors: string[] = [];

  chartDataLabels: string[];
  chartSectionItems: { title: string; sectionUid: string }[];

  chartOptions: ChartConfiguration['options'];
  chartPlugins = [];

  ngOnInit() {
    this.learner$ = this.learner ? of(this.learner) : this.userDetailsData$;

    if (this.chartContent) {
      this.setChartOptions();
      this.showPolarPieChart();
    }
  }

  scrollTo(id: string): void {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  updateChartDataByDates(): void {
    const latestScores = [];
    latestScores.push(this.chartContent.versionedScores.find((score) => this.chartVersionEnd === score.version));
    latestScores.push(this.chartContent.versionedScores.find((score) => this.chartVersionStart === score.version));
    const contentItems = this.chartContent.items.filter((item) => item.scored !== null);
    this.chartData = this.calculateScoresAndGetChartData(latestScores, contentItems);
  }

  private getLocalDateTimeFormat(date: Date): string {
    return LocalTimeHelper.getLocalDateTime(date).format('DD MMMM YYYY, HH:mm');
  }

  private showPolarPieChart(): void {
    const labels: string[] = [];
    const sectionItems: { title: string; sectionUid: string }[] = [];
    const colors: string[] = [];
    const contentItems = this.chartContent.items.filter((item) => item.scored !== null);
    for (const item of contentItems) {
      labels.push(item.title);
      sectionItems.push({ title: item.title, sectionUid: item.sectionUid });
      colors.push(ChartsHelper.getTransparentColor(item.color, 0.5));
      this.chartDataColors.push(item.color);
    }
    const latestScores = cloneDeep(this.chartContent.versionedScores);
    if (latestScores.length > 2) {
      latestScores.length = 2;
    }
    this.chartData = this.calculateScoresAndGetChartData(latestScores, contentItems);
    this.chartDataAlphaColors = colors;
    this.chartDataLabels = labels;
    this.chartSectionItems = sectionItems;

    setTimeout(() => {
      this.updateCirclePoints();
      window.addEventListener(
        'resize',
        () => {
          document.querySelectorAll('.circle-1').forEach((item) => {
            (item as HTMLElement).innerHTML = '';
          });
          this.updateCirclePoints();
        },
        false,
      );
    }, 0);
  }

  private calculateScoresAndGetChartData(latestScores: ChartVersionScore[], contentItems: ChartItem[]): ChartDataset[] {
    const datasets: ChartDataset[] = [];

    latestScores.forEach((versionScore, versionIdx) => {
      const alpha = versionIdx === 0 ? 0.5 : 0.8;
      const isMain = versionIdx === 0;
      const scores: number[] = [];
      const backgroundColors: string[] = [];
      const borderColors: string[] = [];
      const hoverBackgroundColors: string[] = [];
      const hoverBorderColors: string[] = [];
      const hoverBorderWidths: number[] = [];

      // Map content items to their corresponding score
      contentItems.forEach((contentItem) => {
        const color = contentItem.color;
        const scoreItem = versionScore?.scores?.find((item) => item.userScore !== null && contentItem.itemUid === item.tagId);

        if (scoreItem) {
          const score = Math.floor((scoreItem.userScore * 100) / scoreItem.maxScore);
          scores.push(Math.min(score, 100));
          backgroundColors.push(isMain ? ChartsHelper.getTransparentColor(color, alpha) : 'transparent');
          borderColors.push(isMain ? 'transparent' : color);
          hoverBackgroundColors.push(isMain ? ChartsHelper.getTransparentColor(color, 0.3) : 'transparent');
          hoverBorderColors.push(isMain ? 'transparent' : color);
          hoverBorderWidths.push(isMain ? 0 : 2);
        }
      });

      if (scores.length) {
        datasets.push({
          data: scores,
          backgroundColor: backgroundColors,
          hoverBorderColor: hoverBorderColors,
          hoverBorderWidth: hoverBorderWidths,
          hoverBackgroundColor: hoverBackgroundColors,
          borderColor: borderColors,
          borderWidth: isMain ? 0 : 2,
        });
      }
    });

    return datasets;
  }

  private calculatePercentage(version: number, dataIndex: number): string {
    const versionedScore = this.chartContent.versionedScores.find((v) => v.version === version);
    if (!versionedScore) return '';

    const filteredItems = this.chartContent.items.filter((item) => item.scored !== null);
    const chartItem = filteredItems[dataIndex];
    if (!chartItem) return '';
    const scoreItem = versionedScore.scores.find((s) => s.tagId === chartItem.itemUid);

    if (!scoreItem) return '';
    const userScore = scoreItem.userScore || 0;
    const maxScore = scoreItem.maxScore || 1;
    const percentage = Math.round((userScore / maxScore) * 100);

    return `  (${percentage}%)`;
  }

  private setChartOptions(): void {
    /* eslint-disable-next-line @typescript-eslint/no-this-alias */
    const _self = this;
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 5,
          bottom: 5,
        },
      },
      onClick: (evt, items) => {
        if (items && items.length) {
          const currentItem = items[0];

          const chartSectionItemUid = this.chartSectionItems[currentItem.index]?.sectionUid;

          if (chartSectionItemUid) {
            this.scrollTo(chartSectionItemUid);
          }
        }
      },
      scales: {
        r: {
          min: 0,
          max: 100,
          ticks: {
            display: false,
          },
        },
      },
      scale: {
        ticks: {
          min: 0,
          max: 100,
          stepSize: 20,
          display: false,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          enabled: false,
          external: function (context) {
            let tooltipEl = document.getElementById('chartTooltip');
            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'chartTooltip';
              tooltipEl.className = 'chart-tooltip';
              tooltipEl.innerHTML = '<div class="tooltip-content"></div>';
              document.body.appendChild(tooltipEl);
            }
            const tooltipModel = context.tooltip;
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = '0';
              return;
            }

            if (tooltipModel.body) {
              let innerHtml = '<div class="tooltip-content-inner">';
              let tooltipTitles = '<div class="tooltip-titles">';
              const dataPoints = tooltipModel.dataPoints;
              dataPoints.forEach((body, i) => {
                if (
                  _self.chartContent.items[body.dataIndex].icon &&
                  _self.chartContent.items[body.dataIndex].icon.iconImageUrl &&
                  i === 0
                ) {
                  innerHtml += `<div class="tooltip-image">
                    <img src="${_self.chartContent.items[body.dataIndex].icon.iconImageUrl}" alt="" width="30" height="30" /></div>`;
                }
              });
              dataPoints.forEach((body, i) => {
                let percentage = '';
                const label =
                  i === 0
                    ? _self.getLocalDateTimeFormat(
                        _self.chartContent.versionedScores.find((version) => version.version === _self.chartVersionEnd).completedOn,
                      )
                    : _self.getLocalDateTimeFormat(
                        _self.chartContent.versionedScores.find((version) => version.version === _self.chartVersionStart).completedOn,
                      );
                if (_self.chartContent.showPercentage) {
                  percentage =
                    i === 0
                      ? _self.calculatePercentage(_self.chartVersionEnd, body.dataIndex)
                      : _self.calculatePercentage(_self.chartVersionStart, body.dataIndex);
                }

                const colorHtml =
                  i === 0
                    ? `<span class="no-border">
                          <span style="background-color: ${ChartsHelper.getTransparentColor(_self.chartDataColors[body.dataIndex], 0.3)}"></span>
                        </span>`
                    : `<span style="border-color: ${_self.chartDataColors[body.dataIndex]};">
                          <span></span>
                        </span>`;
                tooltipTitles += '<div class="tooltip-title">' + colorHtml + label + percentage + '</div>';
              });
              innerHtml += tooltipTitles + '</div>';

              const tableRoot = tooltipEl.querySelector('div');
              tableRoot.innerHTML = innerHtml;
            }
            // `this` will be the overall tooltip
            // @ts-ignore: Official example from https://www.chartjs.org/docs/latest/configuration/tooltip.html#external-custom-tooltips
            const position = context.chart.canvas.getBoundingClientRect();
            // Display, position, and set styles for font
            tooltipEl.style.opacity = '1';
            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
            tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
          },
          callbacks: {
            // @ts-ignore
            label: (tooltipItem, value) => {
              return `${tooltipItem.label}`;
            },
          },
        },
      },
    };
  }

  private updateCirclePoints(): void {
    const container = this.circlePoints?.nativeElement ? (this.circlePoints.nativeElement as HTMLElement) : '';
    if (!container) {
      return;
    }

    const chartHeight = this.chart?.nativeElement ? (this.chart?.nativeElement as HTMLElement).offsetHeight : '';
    const circleLinesContainer = this.circleLines?.nativeElement ? (this.circleLines.nativeElement as HTMLElement) : '';

    if (!circleLinesContainer || !chartHeight) {
      return;
    }

    container.style.width = chartHeight + 50 + 'px';
    container.style.height = chartHeight + 50 + 'px';
    circleLinesContainer.style.width = chartHeight + 50 + 'px';
    circleLinesContainer.style.height = chartHeight + 50 + 'px';
    const points = container.querySelectorAll('.f_point');
    const width = chartHeight + 50;
    const height = chartHeight + 50;
    const radius = width / 2;
    const chartXCenter = width / 2;
    const chartYCenter = height / 2;
    const step = (2 * Math.PI) / points.length;
    let angle = -0.5 * Math.PI + step / 2;
    points.forEach((point, index) => {
      const element = point as HTMLElement;
      element.style.fontSize = '0.875rem';

      const labelXCoord = Math.round(chartXCenter + radius * Math.cos(angle));
      const delta = chartXCenter > labelXCoord ? element.offsetWidth : 0;
      let labelYCoord = Math.round(chartYCenter + radius * Math.sin(angle) - element.offsetHeight);
      labelYCoord = labelXCoord >= chartXCenter ? labelYCoord + 2 : labelYCoord;

      element.style.left = labelXCoord - delta + 'px';
      element.style.top = labelYCoord + 'px';
      const lineXCoord = labelXCoord;
      const lineYCoord = Math.round(chartYCenter + radius * Math.sin(angle));

      angle += step;

      this.drawLine(chartXCenter, chartYCenter, lineXCoord, lineYCoord, index);
    });
  }

  private drawLine(x1: number, y1: number, x2: number, y2: number, i: number): void {
    if (y1 < y2) {
      let pom = y1;
      y1 = y2;
      y2 = pom;
      pom = x1;
      x1 = x2;
      x2 = pom;
    }

    let a = Math.abs(x1 - x2);
    let b = Math.abs(y1 - y2);
    let c = 0;
    const sx = (x1 + x2) / 2;
    const sy = (y1 + y2) / 2;
    const width = Math.sqrt(a * a + b * b);
    const x = sx - width / 2;
    const y = sy;

    a = width / 2;

    c = Math.abs(sx - x);

    b = Math.sqrt(Math.abs(x1 - x) * Math.abs(x1 - x) + Math.abs(y1 - y) * Math.abs(y1 - y));

    const cosb = (b * b - a * a - c * c) / (2 * a * c);
    const rad = Math.acos(cosb);
    const deg = (rad * 180) / Math.PI;

    const div = document.createElement('div') as HTMLElement;
    div.style.left = x + 'px';
    div.style.top = y + 'px';
    div.style.transform = 'rotate(' + deg + 'deg)';
    div.style.width = width + 'px';
    div.style.backgroundColor = this.chartDataAlphaColors[i];
    div.setAttribute('class', 'line');
    this.circleLines.nativeElement.appendChild(div);
  }
}
